import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import UnitScopeTabs from "../../components/UnitScopeTabs";
import { Clock, ChevronDown, ChevronUp, Check, Send, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import UltimasCompras from "../../components/UltimasCompras";

export default function Retencao() {
  const [unidades, setUnidades] = useState([]);
  const [scope, setScope] = useState("global");
  const [meses, setMeses] = useState(6);
  const [mesesInput, setMesesInput] = useState("6");
  const [lista, setLista] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [confirmEnviado, setConfirmEnviado] = useState(null);
  const [enviandoZapi, setEnviandoZapi] = useState({}); // {cliente_id: true}

  // Envio em massa
  const [massaOpen, setMassaOpen] = useState(false);
  const [massaMensagem, setMassaMensagem] = useState("");
  const [massaBusy, setMassaBusy] = useState(false);
  const [massaResult, setMassaResult] = useState(null);

  useEffect(() => {
    api.get("/unidades").then((r) => setUnidades(r.data));
  }, []);

  const load = () => {
    const params = { meses };
    if (scope !== "global") params.unidade_id = scope;
    api.get("/relatorios/retencao", { params }).then((r) => setLista(r.data));
  };

  useEffect(() => {
    load();
  }, [scope, meses]); // eslint-disable-line

  const aplicarMeses = () => {
    const n = parseInt(mesesInput, 10);
    if (Number.isNaN(n) || n < 1 || n > 240) {
      toast.error("Informe um número entre 1 e 240 meses.");
      return;
    }
    setMeses(n);
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const marcarEnviado = async (c) => {
    try {
      await api.put(`/relatorios/retencao/${c.id}/marcar-enviado`);
      toast.success(`${c.nome} marcado como contatado.`);
      setConfirmEnviado(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao marcar.");
    }
  };

  // Envio individual via Z-API (usa o mesmo endpoint do envio em massa com 1 cliente)
  const enviarZapi = async (c) => {
    if (!c.whatsapp_telefone) {
      toast.error("Este cliente não tem telefone cadastrado.");
      return;
    }
    setEnviandoZapi((s) => ({ ...s, [c.id]: true }));
    try {
      const r = await api.post(
        "/whatsapp/enviar-massa",
        {
          cliente_ids: [c.id],
          mensagem: c.whatsapp_mensagem,
          delay_seconds: 0.1,
        },
        { timeout: 30000 },
      );
      const ok = r.data?.sucesso > 0;
      if (ok) {
        toast.success(`Mensagem enviada para ${c.nome}.`);
        load();
      } else {
        const err = r.data?.resultados?.[0]?.error || "Erro desconhecido";
        toast.error(`Falha: ${err}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erro de rede";
      toast.error(`Não foi possível enviar: ${msg}`);
    } finally {
      setEnviandoZapi((s) => {
        const ns = { ...s };
        delete ns[c.id];
        return ns;
      });
    }
  };

  const abrirEnvioMassa = () => {
    if (lista.length === 0) {
      toast.error("Nenhum cliente na lista atual.");
      return;
    }
    setMassaMensagem(
      "Olá {nome_cliente}, faz um tempo desde sua última troca de óleo no " +
      "{nome_unidade}. Que tal agendar uma revisão? Temos promoções especiais " +
      "esta semana!",
    );
    setMassaResult(null);
    setMassaOpen(true);
  };

  const dispararEnvioMassa = async () => {
    if (!massaMensagem.trim()) {
      toast.error("Digite a mensagem antes de enviar.");
      return;
    }
    setMassaBusy(true);
    try {
      const r = await api.post(
        "/whatsapp/enviar-massa",
        {
          cliente_ids: lista.map((c) => c.id),
          mensagem: massaMensagem,
          delay_seconds: 1.5,
        },
        { timeout: 600000 },
      );
      setMassaResult(r.data);
      toast.success(`Enviados ${r.data.sucesso}/${r.data.total}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no envio em massa");
    } finally {
      setMassaBusy(false);
    }
  };

  const buildWhatsappLink = (c) => {
    const tel = (c.whatsapp_telefone || "").replace(/\D/g, "");
    // Mensagem já vem renderizada pelo backend (template configurável em
    // Configurações → WhatsApp · Mensagem de retenção).
    const msg = encodeURIComponent(c.whatsapp_mensagem || "");
    return tel ? `https://wa.me/${tel}?text=${msg}` : `https://wa.me/?text=${msg}`;
  };

  const copiarLink = async (c) => {
    const link = buildWhatsappLink(c);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        // Fallback para contextos sem clipboard API (iframe/http)
        const ta = document.createElement("textarea");
        ta.value = link;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(`Link copiado — cole em uma nova aba para abrir o WhatsApp de ${c.nome}.`);
    } catch (e) {
      toast.error(`Não foi possível copiar. Link: ${link}`);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
          Reengajamento
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          Retenção de Clientes
        </h2>
      </div>

      <UnitScopeTabs unidades={unidades} value={scope} onChange={setScope} />

      <div className="bg-white border-2 border-neutral-900 p-4 mb-6 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Clientes sem comprar há (meses)
          </label>
          <input
            data-testid="retencao-meses-input"
            type="number"
            min={1}
            max={240}
            value={mesesInput}
            onChange={(e) => setMesesInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && aplicarMeses()}
            className="mt-1 h-10 w-32 px-3 border-2 border-neutral-900 font-mono text-sm"
          />
        </div>
        <button
          data-testid="retencao-aplicar"
          onClick={aplicarMeses}
          className="bg-neutral-950 hover:bg-neutral-800 text-white h-10 px-5 font-display font-bold uppercase text-xs tracking-widest cta-brutal"
        >
          Aplicar
        </button>
        <div className="flex-1" />
        <button
          data-testid="retencao-enviar-massa-btn"
          onClick={abrirEnvioMassa}
          disabled={lista.length === 0}
          className="bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white h-10 px-5 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal"
        >
          <Send className="w-4 h-4" /> Enviar em massa ({lista.length})
        </button>
        <div className="text-right font-mono text-xs text-neutral-600">
          Filtro atual: <span className="font-bold">{meses}</span> meses ·{" "}
          <span className="font-bold">{lista.length}</span> clientes
        </div>
      </div>

      <div className="bg-white border-2 border-neutral-900">
        <div className="p-4 border-b-2 border-neutral-900 flex items-center gap-3">
          <span className="bg-yellow-400 text-neutral-950 px-3 py-1 font-mono text-xs uppercase tracking-widest">
            {lista.length}
          </span>
          <h3 className="font-display font-bold uppercase">
            Sem comprar há {meses} {meses === 1 ? "mês" : "meses"} ou mais
          </h3>
        </div>
        {lista.length === 0 ? (
          <div className="p-8 text-center text-sm text-neutral-500">
            Nenhum cliente nessa faixa (ou todos já foram contatados)
          </div>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {lista.map((c) => (
              <li
                key={c.id}
                data-testid={`retencao-item-${c.id}`}
                className="p-4 flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold">{c.nome}</div>
                  <div className="text-sm text-neutral-600">
                    {c.modelo_veiculo} ·{" "}
                    <span className="font-mono">{c.placa}</span>
                    {(c.celular || c.telefone) && (
                      <> · <span className="font-mono">{c.celular || c.telefone}</span></>
                    )}
                    {c.cpf && <> · CPF <span className="font-mono">{c.cpf}</span></>}
                  </div>
                  <div className="text-xs font-mono text-neutral-500 flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" /> Última troca há{" "}
                    {c.dias_desde_ultima_troca} dias
                  </div>
                  {c.ultimas_compras?.length > 0 && (
                    <div className="mt-2">
                      <UltimasCompras compras={c.ultimas_compras} compact={!expanded[c.id]} />
                      <button
                        data-testid={`retencao-toggle-${c.id}`}
                        onClick={() => toggle(c.id)}
                        className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-neutral-700 hover:text-neutral-950 border-b border-dashed border-neutral-400 pb-0.5"
                      >
                        {expanded[c.id] ? (
                          <>
                            <ChevronUp className="w-3 h-3" /> Ocultar itens
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-3 h-3" /> Ver itens completos
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  <button
                    data-testid={`retencao-copiar-link-${c.id}`}
                    onClick={() => copiarLink(c)}
                    disabled={!c.whatsapp_telefone}
                    title={
                      !c.whatsapp_telefone
                        ? "Cliente sem telefone cadastrado"
                        : "Copiar link do WhatsApp com mensagem personalizada"
                    }
                    className="bg-neutral-950 hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed text-white h-8 px-2.5 font-display font-bold uppercase text-[10px] tracking-widest flex items-center gap-1.5 cta-brutal"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copiar link
                  </button>
                  <button
                    data-testid={`retencao-zapi-${c.id}`}
                    onClick={() => enviarZapi(c)}
                    disabled={enviandoZapi[c.id] || !c.whatsapp_telefone}
                    title={
                      !c.whatsapp_telefone
                        ? "Cliente sem telefone cadastrado"
                        : "Enviar mensagem via Z-API"
                    }
                    className="bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed text-white h-8 px-2.5 font-display font-bold uppercase text-[10px] tracking-widest flex items-center gap-1.5 cta-brutal"
                  >
                    {enviandoZapi[c.id] ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" /> Z-API
                      </>
                    )}
                  </button>
                  <button
                    data-testid={`retencao-marcar-${c.id}`}
                    onClick={() => setConfirmEnviado(c)}
                    title="Marcar como contatado manualmente (sem enviar pelo Z-API)"
                    className="bg-white hover:bg-neutral-100 text-neutral-950 h-8 px-2.5 border-2 border-neutral-900 font-display font-bold uppercase text-[10px] tracking-widest flex items-center gap-1.5"
                  >
                    <Check className="w-3.5 h-3.5" /> Marcar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modal envio em massa */}
      {massaOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-neutral-900 w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b-2 border-neutral-900 bg-green-600 text-white flex items-center justify-between">
              <h3 className="font-display font-black text-lg uppercase">
                Envio em massa via WhatsApp
              </h3>
              <span className="font-mono text-xs">{lista.length} clientes</span>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                  Mensagem (editável)
                </div>
                <textarea
                  data-testid="retencao-massa-mensagem"
                  value={massaMensagem}
                  onChange={(e) => setMassaMensagem(e.target.value)}
                  rows={6}
                  disabled={massaBusy}
                  className="w-full p-3 border-2 border-neutral-900 font-mono text-sm resize-y"
                />
                <div className="mt-1 font-mono text-[10px] text-neutral-500">
                  Placeholders disponíveis:{" "}
                  <code>{"{nome_cliente}"}</code>{" "}
                  <code>{"{nome_unidade}"}</code>{" "}
                  <code>{"{modelo_veiculo}"}</code>{" "}
                  <code>{"{placa}"}</code>
                </div>
              </div>
              <div className="bg-yellow-50 border-2 border-yellow-400 p-3 text-xs">
                ⚠ O envio terá um intervalo de 1.5s entre clientes para evitar bloqueio
                do WhatsApp. Para {lista.length} clientes, o tempo total será de
                aproximadamente {Math.ceil((lista.length * 1.5) / 60)} minuto(s).
                Não feche esta janela durante o envio.
              </div>

              {massaResult && (
                <div className="bg-green-50 border-2 border-green-600 p-3 font-mono text-xs space-y-1">
                  <div className="font-display font-black text-green-800">
                    ✅ Envio concluído: {massaResult.sucesso} sucesso · {massaResult.falha} falha
                  </div>
                  <details>
                    <summary className="cursor-pointer text-green-800">Ver detalhes</summary>
                    <div className="mt-2 max-h-48 overflow-y-auto bg-white p-2 border border-green-300">
                      {massaResult.resultados.map((r, i) => (
                        <div key={i} className={r.ok ? "text-green-700" : "text-red-600"}>
                          {r.ok ? "✓" : "✗"} {r.nome} {r.error ? `— ${r.error}` : ""}
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}
            </div>
            <div className="p-4 border-t-2 border-neutral-900 flex gap-2 justify-end">
              <button
                disabled={massaBusy}
                onClick={() => setMassaOpen(false)}
                className="h-11 px-5 border-2 border-neutral-900 bg-white font-display font-bold uppercase text-xs tracking-widest disabled:opacity-50"
              >
                Fechar
              </button>
              <button
                data-testid="retencao-massa-disparar"
                onClick={dispararEnvioMassa}
                disabled={massaBusy}
                className="bg-green-600 hover:bg-green-700 text-white h-11 px-5 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 disabled:opacity-50 cta-brutal"
              >
                {massaBusy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Disparar para {lista.length}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar marcar como enviado */}
      {confirmEnviado && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-neutral-900 max-w-md w-full">
            <div className="p-4 border-b-2 border-neutral-900 bg-neutral-950 text-white">
              <h3 className="font-display font-black text-lg uppercase">
                Marcar como enviado?
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm">
                <span className="font-bold">{confirmEnviado.nome}</span> será removido
                desta lista. Ele só voltará a aparecer aqui após uma nova compra (e se
                voltar a se enquadrar no filtro de tempo sem comprar).
              </p>
            </div>
            <div className="p-4 border-t-2 border-neutral-900 flex justify-end gap-2">
              <button
                onClick={() => setConfirmEnviado(null)}
                className="h-10 px-4 border-2 border-neutral-900 bg-white font-display font-bold uppercase text-xs tracking-widest"
              >
                Cancelar
              </button>
              <button
                data-testid="retencao-confirmar-enviado"
                onClick={() => marcarEnviado(confirmEnviado)}
                className="bg-neutral-950 hover:bg-neutral-800 text-white h-10 px-5 font-display font-bold uppercase text-xs tracking-widest cta-brutal"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
